import { getHours, getMinutes, getTime } from 'date-fns'
import { format, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'


const MIN_VALID_TIMESTAMP = -8640000000000000 
const MAX_VALID_TIMESTAMP = 8640000000000000  

export const convertDateToLocalDate = (date: Date) => {
  return new Date(date.valueOf() + date.getTimezoneOffset() * 60 * 1000)
}

export const convertDateToTime = (date: Date) => ({
  hours: getHours(date),
  minutes: getMinutes(date)
})

export const formatTime = (num: number): string =>
  num.toString().padStart(2, '0')

export const getLocalTime = (timestamp: number | null, timezone: string) => {
  if (!timestamp) return null
  
  
  if (!Number.isFinite(timestamp) || timestamp < MIN_VALID_TIMESTAMP || timestamp > MAX_VALID_TIMESTAMP) {
    console.warn('Invalid timestamp value:', timestamp)
    return null
  } 
  try {
    const zonedTime = utcToZonedTime(timestamp, timezone)
    return format(zonedTime, 'HH:mm', { timeZone: timezone })
  } catch (error) {
    console.error('Error formatting timestamp:', timestamp, 'with timezone:', timezone, error)
    return null
  }
}

/**
 * Converts a time string (e.g. "09:00") to a UTC timestamp.
 * Uses "today" in the given timezone as the date, so the resulting time-of-day
 * in that timezone matches the timeString (backend validates store hours in city TZ).
 */
export const getTimestampFromTime = (
  timeString: string | null,
  timezone: string
) => {
  if (!timeString) return null

  try {
    // Use Intl so "today" is correct in the target timezone. The old code used
    // toLocaleString + new Date(string), which parses in local TZ and can be wrong.
    const dateStr = new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).format(new Date())
    const dateTimeString = `${dateStr} ${timeString}`
    const result = getTime(zonedTimeToUtc(dateTimeString, timezone))

    if (!Number.isFinite(result)) {
      console.warn('Invalid timestamp result from time string:', timeString)
      return null
    }

    return result
  } catch (error) {
    console.error('Error converting time string to timestamp:', timeString, 'with timezone:', timezone, error)
    return null
  }
}

export const WEEKDAYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday'
] as const

export const sanitizeTimeZone = (timeZone?: string | null): string => {
  const ALGIERS = 'Africa/Algiers'
  
  try {
    const currentUserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    
    if (timeZone && typeof timeZone === 'string' && timeZone.trim()) {
      // Extract IANA timezone name from strings like "Africa/Algiers (GMT+01:00)"
      const cleanedTimeZone = timeZone.replace(/\s*\(.*/, '').trim()
      
      try {
        new Date().toLocaleDateString('en-US', { timeZone: cleanedTimeZone })
        return cleanedTimeZone
      } catch (ex) {
        console.warn('Invalid timezone provided:', timeZone, 'falling back to user timezone')
      }
    }
    if (currentUserTimezone) {
      try {
        new Date().toLocaleDateString('en-US', { timeZone: currentUserTimezone })
        return currentUserTimezone
      } catch (ex) {
        console.warn('User timezone invalid:', currentUserTimezone, 'falling back to Algiers')
      }
    }
    
    return ALGIERS
  } catch (ex) {
    console.warn('Error resolving timezone, falling back to Algiers:', ex)
    return ALGIERS
  }
}

export const getRemainingTimeLeft = (expiryDate: number): string => {
  const diffMs: number = expiryDate - Date.now()
  if (diffMs <= 0) return 'Expired'

  const mins: number = Math.floor(diffMs / 60000)

  if (mins >= 1440) {
    const days = Math.floor(mins / 1440)
    return `${days} day${days > 1 ? 's' : ''} left`
  }

  if (mins >= 241) {
    const hours = Math.floor(mins / 60)
    return `${hours} hours left`
  }

  if (mins >= 61) {
    const hours = Math.floor(mins / 60)
    const minutes = mins % 60
    return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${
      minutes !== 1 ? 's' : ''
    } left`
  }

  if (mins >= 1) return `${mins} minute${mins > 1 ? 's' : ''} left`

  return '1 minute left'
}
